import React, { useState, useCallback, useRef } from 'react';
import {
  ScorecardWidgetInstanceConfig,
  ScorecardDataContent,
  ScoreValue,
} from './types';
import { WidgetEmptyState } from '../../../design';
import { SCORECARD_LAYOUT_COMPONENTS } from './layouts';

interface ScorecardWidgetRendererProps {
  config: ScorecardWidgetInstanceConfig;
  dashboardData?: Record<string, unknown>;
  onDataChange?: (dataKey: string, newData: ScorecardDataContent) => void;
  readOnly?: boolean;
}

const ScorecardWidgetRenderer: React.FC<ScorecardWidgetRendererProps> = ({
  config,
  dashboardData = {},
  onDataChange,
  readOnly = false,
}) => {
  const data = dashboardData[config.dataKey] as ScorecardDataContent | undefined;

  // Default to neutral '=' if no score set
  const initialScore = data?.score ?? '=';
  const [localScore, setLocalScore] = useState<ScoreValue>(initialScore);
  const lastPropScore = useRef(data?.score);

  // Sync from props only when the prop actually changes (server confirmed)
  if (data?.score !== lastPropScore.current) {
    lastPropScore.current = data?.score;
    setLocalScore(data?.score ?? '=');
  }

  const handleSelect = useCallback(
    (value: ScoreValue) => {
      if (readOnly || !onDataChange) return;
      setLocalScore(value);
      onDataChange(config.dataKey, { score: value });
    },
    [readOnly, onDataChange, config.dataKey],
  );

  if (!config.dataKey) {
    return (
      <WidgetEmptyState
        title="No data key configured"
        description="Configure a data key in the widget settings."
      />
    );
  }

  // Pick a layout. Falls back to carousel if the saved variant is unknown
  // (e.g. an older config from before a layout was renamed).
  const LayoutComponent =
    SCORECARD_LAYOUT_COMPONENTS[config.layout ?? 'carousel'] ??
    SCORECARD_LAYOUT_COMPONENTS.carousel;

  return (
    <LayoutComponent
      currentScore={localScore}
      onSelect={handleSelect}
      readOnly={readOnly}
    />
  );
};

export default React.memo(ScorecardWidgetRenderer);
